import React, {useEffect, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Box} from '@mui/system';
import Typography from '@mui/material/Typography';
import $t from 'translation';
import {Link, useNavigate} from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import LoadingButton from '@mui/lab/LoadingButton';
import userApi from 'api/auth';
import {Button, FormControl, FormHelperText} from '@mui/material';
import MetamaskLogin from 'features/auth/components/connect-with-metamask';
import {useCurrentUserContext} from 'features/user/contexts/user-provider';
import {yupResolver} from '@hookform/resolvers/yup';
import schema from './schema';
import isMetaMaskInstalled from 'features/web3/utils/isMetaMaskInstalled';
import styles from './styles';

const LoginForm = () => {
  const navigate = useNavigate();
  const {login, loginGuest} = useCurrentUserContext();
  const hasMetamaskExtension = isMetaMaskInstalled();

  const {register, handleSubmit, control, formState: {errors = {}}} = useForm({
    resolver: yupResolver(schema),
  });

  const [state, setState] = useState({
    loading: false,
    showLoginForm: false,
    errorMessage: '',
  });

  useEffect(() => {
    if (!hasMetamaskExtension) {
      setState({
        ...state,
        showLoginForm: true,
      });
    }
  }, []);

  const showLoginForm = () => {
    setState({
      ...state,
      showLoginForm: true,
    });
  };

  const submit = async data => {
    setState({
      ...state,
      loading: true,
    });

    userApi.login({user: data})
      .then(() => {
        login();
        navigate('/', {replace: true});
      })
      .catch(errorMessage => {
        setState({
          ...state,
          errorMessage,
          loading: false,
          showLoginForm: true,
        });
      });
  };

  const loginAsGuest = () => {
    loginGuest();
    navigate('/', { replace: true });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {$t('auth.login')}
      </Typography>
      <div style={styles.marginBottom}>
        <Typography variant="span" gutterBottom>
          {$t('auth.not.registered')}
        </Typography>{' '}
        <Link to={'/register'}>
          <Typography variant="span" gutterBottom>
            {$t('auth.register')}
          </Typography>
        </Link>
      </div>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <MetamaskLogin
            buttonText={$t('auth.form.login.metamask')}
            handleAction={'loginWithMetamask'}
            showErrorMessage={!state.showLoginForm}
            messageSignature={'Login'}
          />
        </Grid>

        {!state.showLoginForm && (
          <Grid item xs={12}>
            <Button
              data-testid={'login-with-password'}
              variant={'contained'}
              color={'primary'}
              onClick={showLoginForm}
            >
              {$t('auth.form.login')}
            </Button>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            data-testid={'login-guest-button'}
            variant={'contained'}
            color={'primary'}
            onClick={loginAsGuest}
          >
            {$t('auth.login.guest')}
          </Button>
        </Grid>
      </Grid>
      {state.showLoginForm && (
        <form onSubmit={handleSubmit(submit)} noValidate>
          <FormControl error={!!state.errorMessage}>
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <label>{$t('auth.form.email')}</label>
                <TextField
                  data-testid={'login-input-email'}
                  variant="outlined"
                  size={'small'}
                  InputProps={register('email')}
                  error={errors.email}
                  helperText={errors.email && $t(errors.email.message, {field: $t('auth.form.email')})}
                  placeholder={'example@email.com'}
                />
              </Grid>

              <Grid item xs={12}>
                <label>{$t('auth.form.password')}</label>
                <TextField
                  data-testid={'login-input-password'}
                  variant="outlined"
                  size={'small'}
                  type={'password'}
                  InputProps={register('password')}
                  error={errors.password}
                  helperText={errors.password && $t(errors.password.message, {field: $t('auth.form.password')})}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  data-testid={'login-input-remember'}
                  control={
                    <Controller
                      name={'remember_me'}
                      control={control}
                      defaultValue={false}
                      render={({field: props}) => (
                        <Checkbox
                          {...props}
                          checked={props.value}
                        />
                      )}
                    />
                  }
                  label={$t('auth.form.remember')}
                />
              </Grid>

              <Grid item xs={12}>
                <Link to={'/forgot-password'}>
                  <Typography variant="span" gutterBottom>
                    {$t('auth.forgot.password')}
                  </Typography>
                </Link>
              </Grid>

              <Grid item xs={12}>
                <FormHelperText>{$t(state.errorMessage)}</FormHelperText>
              </Grid>

              <Grid item xs={12}>
                <LoadingButton
                  data-testid={'login-button'}
                  loading={state.loading}
                  loadingPosition={'start'}
                  startIcon={' '} // To prevent silly warning
                  type={'submit'}
                  variant={'contained'}
                  color={'primary'}
                >
                  {$t('auth.form.login')}
                </LoadingButton>
              </Grid>
            </Grid>
          </FormControl>
        </form>
      )}
    </Box>
  );
};

export default LoginForm;